import { useEffect, useLayoutEffect, useRef, useState } from "react";

export const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const signed = (v, d = 1) => (v == null ? "—" : `${v >= 0 ? "+" : "−"}${Math.abs(Number(v)).toFixed(d)}`);

export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const shortDate = (iso, withYear = false) => {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${+d} ${MONTHS[+m - 1]}${withYear ? ` ${y}` : ""}`;
};

export const initials = (name) =>
  (name || "?").replace(/[^A-Za-z ]/g, "").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

export const SIZE_LABEL = { large: "large caps", mid: "mid caps", small: "small caps", all: "all sizes" };
export const SIZE_ADJ = { large: "large-cap", mid: "mid-cap", small: "small-cap", all: "" };

// Plain-language signal grade from the 1-10 score band (10 = strongest).
const SIGNAL_WORDS = ["Unrated", "Weak", "Weak", "Below average", "Below average", "Average", "Average", "Above average", "Above average", "Strong", "Strong"];
export const signalWord = (d) => SIGNAL_WORDS[d] || "Unrated";
export const signalRank = (d) => (!d ? "" : d >= 6 ? `Top ${(11 - d) * 10}%` : `Bottom ${d * 10}%`);
export const signalClass = (d) => (d >= 7 ? "pill good" : d <= 4 ? "pill bad" : "pill");
export const yearSpan = (q) => (q || "").replace(/^(\d{4})Q\d\.\.(\d{4})Q\d$/, "$1–$2");

export const REGIME_NEXT = { rises: "keeps rising", levels: "levels off", dips: "dips" };

export const PHASE = { strong: ["Strong", "good"], neutral: ["Neutral", ""], weak: ["Weak", "warn"] };

export const REGIME_BEHAVIOR = {
  0: "Strong early performance, then decay.",
  1: "Smooth, steady upward slope.",
  2: "Initial drop, then a strong rebound.",
  3: "Mid-period peak, then a sharp decline.",
  4: "Weak start, strong late acceleration.",
  5: "Fast rise, then holds its gains.",
  6: "Flat, then a sudden strong breakout.",
  7: "Continuous decline after the buying.",
  8: "Gradual acceleration over time.",
};

export function useNarrow(px = 600) {
  const [narrow, setNarrow] = useState(typeof window !== "undefined" && window.innerWidth < px);
  useEffect(() => {
    const on = () => setNarrow(window.innerWidth < px);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, [px]);
  return narrow;
}

export const linePath = (pts) => pts.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");

export const niceScale = (lo, hi, n = 4) => {
  const raw = (hi - lo || 1) / (n - 1);
  const mag = 10 ** Math.floor(Math.log10(raw));
  const near = (a, b) => (Math.abs(Math.log(b / raw)) < Math.abs(Math.log(a / raw)) ? b : a);
  let step = [1, 2, 2.5, 5, 10].map((m) => m * mag).reduce(near), ticks = [];
  for (let k = 0; k < 3 && ticks.length < n - 1; k++, step /= 2) {
    ticks = [];
    for (let v = Math.ceil(lo / step) * step; v <= hi + 1e-9; v += step) ticks.push(+v.toFixed(6));
  }
  return { min: lo, max: hi, ticks };
};

export const padRange = (lo, hi, f = 0.08) => { const p = (hi - lo || 1) * f; return [lo - p, hi + p]; };

export const pctTick = (v) => `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(v)}%`;

export const ordinal = (n) => { const s = ["th", "st", "nd", "rd"], v = n % 100; return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`; };

export const qLabel = (q) => (q || "").replace(/^(\d{4})Q(\d)$/, "Q$2 $1");

// Container width so SVG charts draw 1:1 (readable text at every screen size).
export function useBoxWidth(fallback) {
  const ref = useRef(null);
  const [w, setW] = useState(fallback);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setW(Math.round(el.clientWidth) || fallback);
    const ro = new ResizeObserver(([e]) => { const v = Math.round(e.contentRect.width); if (v) setW(v); });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fallback]);
  return [ref, w];
}

export const money = (v) => (Number.isInteger(v) ? `$${v}` : `$${v >= 100 ? v.toFixed(0) : v.toFixed(v >= 10 ? 1 : 2)}`);

export const bookPct = (v) => (v == null ? "—" : `${v.toFixed(v >= 1 ? 1 : 2)}%`);

export const MIN_BOOK_PCT = 2;

export const WORDS = ["No", "One", "Two", "Three", "Four", "Five"];
